"use client";

import { Loader2, UploadCloud, X } from "lucide-react";
import Image from "next/image";
import { useCallback, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { toast } from "sonner";

import { useUploadThing } from "@/lib/uploadthing";
import { cn } from "@/lib/utils";

interface ImageUploadProps {
	onChange: (url?: string) => void;
	value?: string;
	disabled?: boolean;
	className?: string;
	compact?: boolean;
}

export function ImageUpload({
	onChange,
	value,
	disabled,
	className,
	compact = false,
}: ImageUploadProps) {
	const [isUploading, setIsUploading] = useState(false);
	const [previewUrl, setPreviewUrl] = useState<string | null>(null);

	useEffect(() => {
		const currentPreviewUrl = previewUrl;
		return () => {
			if (currentPreviewUrl) {
				URL.revokeObjectURL(currentPreviewUrl);
			}
		};
	}, [previewUrl]);

	const { startUpload } = useUploadThing("imageUploader", {
		onClientUploadComplete: (res) => {
			setIsUploading(false);
			if (previewUrl) {
				URL.revokeObjectURL(previewUrl);
				setPreviewUrl(null);
			}
			if (res?.[0]?.ufsUrl) {
				onChange(res[0].ufsUrl);
				toast.success("Image uploaded successfully!");
			} else {
				toast.error("Upload completed but no URL was returned.");
			}
		},
		onUploadError: (error) => {
			setIsUploading(false);
			if (previewUrl) {
				URL.revokeObjectURL(previewUrl);
				setPreviewUrl(null);
			}
			console.error("Upload error:", error);
			toast.error(`Failed to upload image: ${error.message}`);
		},
		onUploadBegin: () => {
			setIsUploading(true);
		},
	});

	const onDrop = useCallback(
		async (acceptedFiles: File[]) => {
			if (disabled || isUploading) return;

			setIsUploading(true);

			if (previewUrl) {
				URL.revokeObjectURL(previewUrl);
				setPreviewUrl(null);
			}

			const heic2any = (await import("heic2any").catch(() => null))?.default;
			let filesToUpload = acceptedFiles;

			if (heic2any) {
				try {
					filesToUpload = await Promise.all(
						acceptedFiles.map(async (file) => {
							const isHeic =
								file.type === "image/heic" ||
								file.name.toLowerCase().endsWith(".heic");
							if (isHeic) {
								toast.info("Converting HEIC image...");
								const blob = await heic2any({
									blob: file,
									toType: "image/jpeg",
									quality: 0.8,
								});
								const convertedFile = new File(
									[blob as Blob],
									`${file.name.split(".")[0]}.jpg`,
									{ type: "image/jpeg" },
								);
								toast.success("HEIC conversion successful.");
								return convertedFile;
							}
							return file;
						}),
					);
				} catch (conversionError) {
					console.error("HEIC Conversion error:", conversionError);
					toast.error(
						"Failed to convert HEIC image. Please try a different format.",
					);
					setIsUploading(false);
					return;
				}
			}

			if (filesToUpload.length > 0) {
				const fileForPreview = filesToUpload[0];
				const objectUrl = URL.createObjectURL(fileForPreview);
				setPreviewUrl(objectUrl);

				await startUpload(filesToUpload);
			} else {
				setIsUploading(false);
			}
		},
		[startUpload, disabled, isUploading, previewUrl],
	);

	const { getRootProps, getInputProps, isDragActive } = useDropzone({
		onDrop,
		accept: {
			"image/*": [".png", ".jpeg", ".jpg", ".webp", ".heic"],
		},
		multiple: false,
		disabled: disabled || isUploading,
	});

	const handleRemove = () => {
		if (disabled || isUploading) return;
		if (previewUrl) {
			URL.revokeObjectURL(previewUrl);
			setPreviewUrl(null);
			onChange(undefined);
		}
	};

	if (previewUrl) {
		return (
			<div
				className={cn(
					"relative overflow-hidden rounded-lg",
					compact && "w-24",
					"aspect-[2/3]",
					className,
				)}
			>
				<Image
					src={previewUrl}
					alt="Image Preview"
					fill
					className="absolute inset-0 h-full w-full object-cover shadow-md"
				/>
				<button
					onClick={handleRemove}
					className={cn(
						"absolute z-10 rounded-full bg-rose-500 text-white shadow-md transition hover:scale-110 hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-50",
						!compact && "right-1.5 top-1.5 p-2",
						compact && "right-0.5 top-0.5 p-1.5",
					)}
					type="button"
					disabled={disabled || isUploading}
					aria-label="Remove image"
				>
					<X className={cn(!compact && "h-4 w-4", compact && "h-3 w-3")} />
				</button>
				{isUploading && (
					<div className="absolute inset-0 flex items-center justify-center rounded-lg bg-black/60">
						<Loader2
							className={cn(
								"animate-spin text-white",
								!compact && "h-10 w-10",
								compact && "h-6 w-6",
							)}
						/>
					</div>
				)}
			</div>
		);
	}

	return (
		<div
			{...getRootProps()}
			className={cn(
				"relative flex cursor-pointer flex-col items-center justify-center overflow-hidden rounded-lg border-2 border-dashed border-gray-300 text-center text-gray-500 transition-colors duration-200 ease-in-out hover:border-gray-400 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-400 dark:hover:border-gray-500 dark:hover:bg-gray-800/50",
				compact && "w-24 p-2",
				"aspect-[2/3]",
				isDragActive &&
					"border-indigo-500 bg-indigo-50 ring-2 ring-indigo-300 dark:border-indigo-400 dark:bg-indigo-900/30 dark:ring-indigo-500",
				(disabled || isUploading) &&
					"cursor-not-allowed bg-gray-100 opacity-60 dark:bg-gray-800",
				className,
			)}
		>
			<input {...getInputProps()} />
			<div className="absolute inset-0 flex flex-col items-center justify-center space-y-1 p-2">
				{isUploading ? (
					<>
						<Loader2
							className={cn(
								"animate-spin",
								!compact && "h-10 w-10 sm:h-12 sm:w-12",
								compact && "h-6 w-6",
							)}
						/>
						<p
							className={cn(
								"font-medium",
								!compact && "text-sm sm:text-base",
								compact && "text-xs",
							)}
						>
							Uploading...
						</p>
					</>
				) : isDragActive ? (
					<>
						<UploadCloud
							className={cn(
								"text-indigo-500",
								!compact && "mb-2 h-10 w-10 sm:h-12 sm:w-12",
								compact && "mb-1 h-6 w-6",
							)}
						/>
						<p
							className={cn(
								"font-medium",
								!compact && "text-sm sm:text-base",
								compact && "text-xs",
							)}
						>
							Drop the image here...
						</p>
					</>
				) : (
					<>
						<UploadCloud
							className={cn(
								!compact && "mb-2 h-10 w-10 sm:h-12 sm:w-12",
								compact && "mb-1 h-6 w-6",
							)}
						/>
						<p
							className={cn(
								"font-medium",
								!compact && "text-sm sm:text-base",
								compact && "text-xs",
							)}
						>
							Drag & drop or click
						</p>
						{!compact && (
							<p className="text-xs text-gray-400 dark:text-gray-500">
								Supports JPG, PNG, WEBP, HEIC
							</p>
						)}
					</>
				)}
			</div>
		</div>
	);
}
